const CONTROL_OR_INVISIBLE_PATTERN = /[\p{Cc}\p{Cf}\p{Cs}]/u;
const HTML_OR_SCRIPT_PATTERN =
  /[<>]|&(?:amp;)*(?:lt|gt|#0*60|#x0*3c);|%0*3c|\\u0*3c|\bon\p{L}+\s*=|\b(?:javascript|vbscript)\s*:|\bdata\s*:\s*text\/html|\bexpression\s*\(|\b(?:eval|alert|prompt|confirm|setTimeout|setInterval)\s*\(|\b(?:document|window)\s*\./iu;
const EXCEL_FORMULA_PATTERN = /^\s*[=+@-]/u;

export const standardInputMessage = (label: string) =>
  `${label} contains unsafe input`;

function nestedFieldLabel(field: string) {
  return field
    .replace(
      /nominees\[(\d+)\]/u,
      (_match, index) => `Nominee ${Number(index) + 1}`
    )
    .replace(
      /dematAccounts\[(\d+)\]/u,
      (_match, index) => `Demat account ${Number(index) + 1}`
    )
    .replace(/\.guardian\b/u, " guardian")
    .replace(/\.dematAccountId\b/u, " account ID")
    .replace(/\.fileName\b/u, " file name")
    .replace(/\./gu, " ");
}

export function isValidStandardInput(
  value: unknown,
  options: { excel?: boolean } = {}
): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (typeof value !== "string") return true;
  const normalized = value.normalize("NFKC");
  if (
    CONTROL_OR_INVISIBLE_PATTERN.test(normalized) ||
    HTML_OR_SCRIPT_PATTERN.test(normalized) ||
    (options.excel && EXCEL_FORMULA_PATTERN.test(normalized))
  ) {
    return false;
  }
  return true;
}

export function areValidStandardTags(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (!Array.isArray(value)) return isValidStandardInput(value);
  return value.every(
    (tag) =>
      isValidStandardInput(tag?.label) && isValidStandardInput(tag?.value)
  );
}

function nomineeIssues(nominee: unknown, prefix: string): StandardInputIssue[] {
  if (!nominee || typeof nominee !== "object") return [];
  const data = nominee as Record<string, unknown>;
  const fields: Array<[string, unknown]> = [
    ["name", data.name],
    ["relationship", data.relationship],
    ["contact", data.contact],
    ["email", data.email],
    ["address", data.address],
  ].map(([field, value]) => [`${prefix}.${field}`, value]);
  return [
    ...fields.flatMap(([field, value]) =>
      isValidStandardInput(value)
        ? []
        : [{ field, message: standardInputMessage(nestedFieldLabel(field)) }]
    ),
    ...nomineeIssues(data.guardian, `${prefix}.guardian`),
  ];
}

function dematIssues(demat: unknown, prefix: string): StandardInputIssue[] {
  if (!demat || typeof demat !== "object") return [];
  const data = demat as Record<string, unknown>;
  return (
    [
      ["dematAccountId", data.dematAccountId],
      ["type", data.type],
      ["status", data.status],
      ["fileName", data.fileName],
    ] as Array<[string, unknown]>
  ).flatMap(([field, value]) => {
    const path = `${prefix}.${field}`;
    return isValidStandardInput(value)
      ? []
      : [
          {
            field: path,
            message: standardInputMessage(nestedFieldLabel(path)),
          },
        ];
  });
}

type StandardInputIssue = {
  field: string;
  message: string;
};

export function validateEmployeeStandardFields(
  input: object
): StandardInputIssue[] {
  const data = input as Record<string, unknown>;
  const fields: Array<[string, unknown]> = [
    ["employeeName", data.employeeName ?? data.name],
    ["legalName", data.legalName],
    ["employeeIdentification", data.employeeIdentification ?? data.hrId],
    ["employeeDesignation", data.employeeDesignation ?? data.designation],
    ...[
      "department",
      "manager",
      "grade",
      "company",
      "subsidiary",
      "nationality",
      "residentialStatus",
      "workingCountry",
      "address",
      "registeredAddress",
      "notes",
      "nominee1",
      "nominee2",
      "relationship1",
      "relationship2",
      "email",
      "personalEmail",
      "email1",
      "email2",
      "contactNumber",
      "contact1",
      "contact2",
      "pan",
      "dematAccountId",
    ].map((field): [string, unknown] => [field, data[field]]),
  ];

  return [
    ...fields.flatMap(([field, value]) =>
      isValidStandardInput(value)
        ? []
        : [{ field, message: standardInputMessage(field) }]
    ),
    ...(Array.isArray(data.nominees)
      ? data.nominees.flatMap((nominee, index) =>
          nomineeIssues(nominee, `nominees[${index}]`)
        )
      : []),
    ...(Array.isArray(data.dematAccounts)
      ? data.dematAccounts.flatMap((demat, index) =>
          dematIssues(demat, `dematAccounts[${index}]`)
        )
      : []),
  ];
}

export function getServerValidationErrors(error: any): Record<string, string> {
  const entries = error?.response?.data?.errorsValidation;
  return Array.isArray(entries) ? Object.assign({}, ...entries) : {};
}

export const getServerErrorMessage = (error: any) =>
  error?.response?.data?.errorMessage ||
  error?.response?.data?.errorRaw ||
  error?.response?.data?.reason ||
  "Invalid input";
